import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { useMsal } from '@azure/msal-react';
import { useNotificationService } from '@providers';
import { formatObject } from '@utils';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { loginRequest } from '../../auth/msalConfig';
import { QueryResult } from './QueryResult';

interface UseFetchOptions {
  method?: 'GET' | 'POST';
  headers?: Record<string, string>;
  body?: unknown;
  enabled?: boolean;
}

export interface UseFetchResult<T> {
  result: T | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<T | null>;
}

const BASE_URL = import.meta.env.VITE_BACKEND_BASE_URL;

// Helper hook to get access token
function useAccessToken() {
  const { instance, accounts } = useMsal();

  const getAccessToken = useCallback(async () => {
    if (accounts.length === 0) {
      return null;
    }

    try {
      const response = await instance.acquireTokenSilent({
        ...loginRequest,
        account: accounts[0],
      });
      return response.accessToken;
    } catch (error) {
      console.error('Failed to acquire token silently:', error);
      if (error instanceof InteractionRequiredAuthError) {
        await instance.acquireTokenRedirect({
          ...loginRequest,
          account: accounts[0],
        });
      }
      return null;
    }
  }, [instance, accounts]);

  return getAccessToken;
}

export function useFetch<T = unknown>(
  url: string,
  options: UseFetchOptions = {},
): UseFetchResult<T> {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const getAccessToken = useAccessToken();
  const instance = useMsal().instance;

  const { method = 'GET', headers, body, enabled = true } = options;
  const abortControllerRef = useRef<AbortController | null>(null);

  const headersRef = useRef(headers);
  const bodyRef = useRef(body);

  if (JSON.stringify(headersRef.current) !== JSON.stringify(headers)) {
    headersRef.current = headers;
  }
  if (JSON.stringify(bodyRef.current) !== JSON.stringify(body)) {
    bodyRef.current = body;
  }

  const fetchData = useCallback(
    async (forceEnabled = false): Promise<T | null> => {
      if (!enabled && !forceEnabled) return null;

      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      abortControllerRef.current = new AbortController();

      setLoading(true);
      setError(null);

      let isAborted = false;
      let result: T | null = null;

      try {
        // Get access token
        const accessToken = await getAccessToken();

        const fetchOptions: RequestInit = {
          method,
          headers: {
            'Content-Type': 'application/json',
            ...(accessToken && { Authorization: `Bearer ${accessToken}` }),
            ...(headersRef.current || {}),
          },
          signal: abortControllerRef.current.signal,
        };

        if (bodyRef.current && method !== 'GET') {
          fetchOptions.body = JSON.stringify(bodyRef.current);
        }

        const response = await fetch(`${BASE_URL}/${url}`, fetchOptions);

        if (!response.ok) {
          if (response.status === 401) {
            await instance.logoutRedirect();
          }
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        result = await response.json();
        setData(result);
      } catch (err) {
        if (err instanceof Error && err.name === 'AbortError') {
          isAborted = true;
          return null;
        }
        setError(err instanceof Error ? err.message : 'Unknown error occurred');
      } finally {
        if (!isAborted) {
          setLoading(false);
        }
      }

      return result;
    },
    [url, method, enabled, getAccessToken, instance],
  );

  useEffect(() => {
    fetchData(false).catch((error) => {
      console.error('Initial fetch failed:', error);
    });
  }, [fetchData]);

  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  const refetch = useCallback(async (): Promise<T | null> => {
    return await fetchData(true); // Force enabled for manual refetch
  }, [fetchData]);

  return useMemo(
    () => ({ result: data, loading, error, refetch }),
    [data, loading, error, refetch],
  );
}

interface UseMutationOptions {
  method?: 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  headers?: Record<string, string>;
  onSuccess?: (data: unknown) => void;
  onError?: (error: string) => void;
  showSuccessNotification?: boolean;
  showErrorNotification?: boolean;
  successMessage?: string;
  errorMessage?: string;
}

export interface UseMutationResult<T> {
  mutate: (
    data?: unknown,
    endpointFormatObj?: Record<string, string | undefined>,
  ) => Promise<QueryResult<T> | null>;
  loading: boolean;
  error: string | null;
  reset: () => void;
}

function useMutation<T = unknown>(
  url: string,
  options: UseMutationOptions = {},
): UseMutationResult<T> {
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const { t } = useTranslation();
  const notificationService = useNotificationService();
  const getAccessToken = useAccessToken();
  const instance = useMsal().instance;

  const {
    method = 'POST',
    headers = {},
    onSuccess,
    onError,
    showSuccessNotification = false,
    showErrorNotification = false,
    successMessage,
    errorMessage: customErrorMessage,
  } = options;

  const mutate = useCallback(
    async (
      requestData?: unknown,
      endpointFormatObj?: Record<string, string | undefined>,
    ) => {
      setLoading(true);
      setError(null);

      try {
        // Get access token
        const accessToken = await getAccessToken();

        const fetchOptions: RequestInit = {
          method,
          headers: {
            'Content-Type': 'application/json',
            ...(accessToken && { Authorization: `Bearer ${accessToken}` }),
            ...headers,
          },
        };

        if (requestData) {
          fetchOptions.body = JSON.stringify(requestData);
        }

        const response = await fetch(
          `${BASE_URL}/${formatObject(url, endpointFormatObj)}`,
          fetchOptions,
        );

        if (!response.ok) {
          if (response.status === 401) {
            await instance.logoutRedirect();
          }
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        let result = null;
        if (response.status !== 204) {
          result = await response.json();
        }

        // Show success notification if enabled
        if (showSuccessNotification && notificationService) {
          const message = successMessage || t('notifications.operationSuccess');
          notificationService.showSuccess(message);
        }

        if (onSuccess) {
          onSuccess(result);
        }
        return result;
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Unknown error occurred';
        setError(errorMessage);

        // Show error notification if enabled
        if (showErrorNotification && notificationService) {
          const message =
            customErrorMessage || t('notifications.operationError');
          notificationService.showError(message);
        }

        if (onError) {
          onError(errorMessage);
        }
      } finally {
        setLoading(false);
      }
    },
    [
      url,
      method,
      headers,
      onSuccess,
      onError,
      notificationService,
      showSuccessNotification,
      showErrorNotification,
      successMessage,
      customErrorMessage,
      t,
      getAccessToken,
      instance,
    ],
  );

  const reset = useCallback(() => {
    setError(null);
  }, []);

  return { mutate, loading, error, reset };
}

// Convenience functions for common CRUD operations with notifications
// eslint-disable-next-line @typescript-eslint/no-unused-vars
function useCreateMutation<T = unknown>(
  url: string,
  onSuccess?: () => void,
  customSuccessMessage?: string,
): UseMutationResult<T> {
  const { t } = useTranslation();

  return useMutation<T>(url, {
    method: 'POST',
    onSuccess,
    showSuccessNotification: true,
    successMessage: customSuccessMessage || t('notifications.createSuccess'),
    showErrorNotification: true,
  });
}

export function useUpdateMutation<T = unknown>(
  url: string,
  mutationOptions?: UseMutationOptions,
): UseMutationResult<T> {
  const { t } = useTranslation();

  return useMutation<T>(url, {
    showSuccessNotification: true,
    showErrorNotification: true,
    method: 'PUT',
    successMessage: t('notifications.updateSuccess'),
    ...(mutationOptions ?? {}),
  });
}

export interface UseDeleteMutationResult<T> {
  mutate: (
    endpointFormatObj?: Record<string, string>,
  ) => Promise<QueryResult<T> | null>;
  loading: boolean;
  error: string | null;
}

export function useDeleteMutation<T = unknown>(
  url: string,
  onSuccess?: () => void,
  customSuccessMessage?: string,
): UseDeleteMutationResult<T> {
  const { t } = useTranslation();

  const baseMutation = useMutation<T>(url, {
    method: 'DELETE',
    onSuccess,
    showSuccessNotification: true,
    successMessage: customSuccessMessage || t('notifications.deleteSuccess'),
    showErrorNotification: true,
  });

  return {
    ...baseMutation,
    mutate: (endpointFormatObj?: Record<string, string>) =>
      baseMutation.mutate(undefined, endpointFormatObj),
  };
}
